import React, { useState } from 'react';
import { RackStructure, compareSegments } from '../rack-builder/types';
import { LocationHeatmapDto } from './types';
import { Activity, TrendingUp, Calendar } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface HeatmapRackGridProps {
    rackStructure: RackStructure;
    heatmapData: Map<string, LocationHeatmapDto>;
}

/**
 * HeatmapRackGrid Component
 * 
 * Displays rack locations with heat colors based on activity levels
 * - X-axis (horizontal): Verticals (V01, V02, V03, ...) from left to right
 * - Y-axis (vertical): Shelves (H09, H08, ..., H01, H00) from top to bottom (highest shelf at top)
 */
export const HeatmapRackGrid: React.FC<HeatmapRackGridProps> = ({
    rackStructure,
    heatmapData
}) => {
    const { t } = useTranslation();
    const [hoveredLocation, setHoveredLocation] = useState<LocationHeatmapDto | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

    // Create a map for quick lookup
    const locationMap = new Map<string, any>();
    rackStructure.locations.forEach((loc) => {
        const key = `${loc.vertical}-${loc.shelf}`;
        locationMap.set(key, loc);
    });

    // Sort shelves in DESCENDING order (highest shelf at top)
    const sortedShelves = [...rackStructure.shelves].sort((a, b) => compareSegments(b, a));

    // Sort verticals in ascending order (left to right)
    const sortedVerticals = [...rackStructure.verticals].sort(compareSegments);

    /**
     * Get location by grid position
     */
    const getLocationAt = (vertical: string, shelf: string) => {
        const key = `${vertical}-${shelf}`;
        return locationMap.get(key);
    };

    /**
     * Get heatmap data for location
     */
    const getHeatmapForLocation = (binCode: string): LocationHeatmapDto | undefined => {
        return heatmapData.get(binCode);
    };

    /**
     * Get cell background color based on heat level
     */
    const getCellBackgroundColor = (binCode: string): string => {
        const heatData = getHeatmapForLocation(binCode);
        if (!heatData) {
            return 'bg-gray-100';
        }
        return '';
    };

    /**
     * Handle cell hover for tooltip
     */
    const handleCellHover = (heatData: LocationHeatmapDto | undefined, event: React.MouseEvent) => {
        if (heatData) {
            setHoveredLocation(heatData);
            setTooltipPosition({ x: event.clientX, y: event.clientY });
        }
    };

    const handleCellLeave = () => {
        setHoveredLocation(null);
        setTooltipPosition(null);
    };

    /**
     * Format date for display
     */
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('nb-NO', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    if (rackStructure.locations.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <p>{t('manager.modules.locationHeatmap.grid.noLocationsToShow', {}, 'Ingen lokasjoner å vise')}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Grid container */}
            <div 
                className="w-full h-full border border-gray-300 bg-white rounded-lg p-4 overflow-auto"
                style={{ userSelect: 'none' }}
            >
                {/* Header with vertical numbers */}
                <div className="flex mb-3 sticky top-0 bg-white z-10 border-b border-gray-200 pb-1">
                    <div className="w-20 flex-shrink-0" /> {/* Spacer for shelf labels */}
                    {sortedVerticals.map(vertical => (
                        <div 
                            key={vertical}
                            className="w-36 text-center font-semibold text-base text-gray-700 flex-shrink-0"
                        >
                            V{/^\d+$/.test(vertical) ? vertical.padStart(2, '0') : vertical}
                        </div>
                    ))}
                </div>

                {/* Rack visualization */}
                <div className="space-y-1.5">
                    {sortedShelves.map((shelf) => (
                        <div key={shelf} className="flex items-center">
                            {/* Shelf label */}
                            <div className="w-20 text-right pr-4 font-semibold text-base text-gray-700 flex-shrink-0 sticky left-0 bg-white z-5">
                                H{/^\d+$/.test(shelf) ? shelf.padStart(2, '0') : shelf}
                            </div>

                            {/* Cells for this shelf */}
                            {sortedVerticals.map(vertical => {
                                const location = getLocationAt(vertical, shelf);
                                const heatData = location ? getHeatmapForLocation(location.binCode) : undefined;
                                const bgColorClass = location ? getCellBackgroundColor(location.binCode) : 'bg-gray-50';

                                return (
                                    <div
                                        key={`${vertical}-${shelf}`}
                                        className={`w-36 h-30 relative border transition-all flex flex-col items-center justify-center p-2 text-sm flex-shrink-0 ${
                                            location 
                                                ? 'border-gray-300 cursor-pointer hover:shadow-lg'
                                                : 'border-dashed border-gray-200'
                                        } ${bgColorClass}`}
                                        style={heatData ? { backgroundColor: heatData.heatColor } : undefined}
                                        onMouseMove={(e) => handleCellHover(heatData, e)}
                                        onMouseLeave={handleCellLeave}
                                    >
                                        {location ? (
                                            <>
                                                {/* BinCode */}
                                                <div className="font-mono font-semibold text-sm text-gray-800">
                                                    {location.binCode}
                                                </div>

                                                {/* Heat level indicator */}
                                                {heatData && (
                                                    <div className="flex items-center justify-center space-x-1 mt-1.5 h-6">
                                                        <Activity className="w-4 h-4 text-white" />
                                                        <span className="text-xs font-bold text-white">
                                                            {t('manager.modules.locationHeatmap.cell.level', { '0': heatData.heatLevel }, `Niv ${heatData.heatLevel}`)}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Transaction count */}
                                                {heatData && (
                                                    <div className="text-xs text-white font-medium mt-1">
                                                        {t('manager.modules.locationHeatmap.cell.transShort', { '0': heatData.transactionCount }, `${heatData.transactionCount} trans.`)}
                                                    </div>
                                                )}

                                                {/* No activity indicator */}
                                                {!heatData && (
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {t('manager.modules.locationHeatmap.cell.noActivity', {}, 'Ingen aktivitet')}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-gray-400 text-[8px]">-</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Tooltip */}
            {hoveredLocation && tooltipPosition && (
                <div
                    className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-xl text-xs max-w-xs"
                    style={{
                        left: tooltipPosition.x + 10,
                        top: tooltipPosition.y + 10,
                        pointerEvents: 'none'
                    }}
                >
                    <div className="font-bold mb-2 text-base">{hoveredLocation.binCode}</div>
                    
                    <div className="space-y-1.5 text-gray-200">
                        <div className="flex items-center space-x-2">
                            <Activity className="w-4 h-4" />
                            <span className="font-semibold">{t('manager.modules.locationHeatmap.tooltip.activityLevel', { '0': hoveredLocation.heatLevel }, `Aktivitetsnivå: ${hoveredLocation.heatLevel}/5`)}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <TrendingUp className="w-4 h-4" />
                            <span>{t('manager.modules.locationHeatmap.tooltip.transactions', { '0': hoveredLocation.transactionCount }, `Transaksjoner: ${hoveredLocation.transactionCount}`)}</span>
                        </div>

                        <div className="text-gray-300">
                            {t('manager.modules.locationHeatmap.tooltip.totalQuantity', { '0': hoveredLocation.totalQuantity.toFixed(2) }, `Total mengde: ${hoveredLocation.totalQuantity.toFixed(2)}`)}
                        </div>

                        <div className="text-gray-300">
                            {t('manager.modules.locationHeatmap.tooltip.activeDays', { '0': hoveredLocation.uniqueDays, '1': hoveredLocation.periodDays }, `Aktive dager: ${hoveredLocation.uniqueDays} av ${hoveredLocation.periodDays}`)}
                        </div>

                        {hoveredLocation.avgTransPerDay && (
                            <div className="text-gray-300">
                                {t('manager.modules.locationHeatmap.tooltip.avgTransPerDay', { '0': hoveredLocation.avgTransPerDay.toFixed(2) }, `Gj.snitt trans/dag: ${hoveredLocation.avgTransPerDay.toFixed(2)}`)}
                            </div>
                        )}
                        
                        <div className="border-t border-gray-700 pt-1.5 mt-1.5">
                            <div className="flex items-center space-x-2">
                                <Calendar className="w-3 h-3" />
                                <span className="text-gray-400 text-[10px]">
                                    {formatDate(hoveredLocation.firstTransaction)} → {formatDate(hoveredLocation.lastTransaction)}
                                </span>
                            </div>
                        </div>
                        
                        <div className="text-yellow-300 font-semibold mt-2">
                            {t('manager.modules.locationHeatmap.tooltip.percentile', { '0': hoveredLocation.heatPercentile.toFixed(1) }, `Persentil: ${hoveredLocation.heatPercentile.toFixed(1)}%`)}
                        </div>
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-700 mb-2">{t('manager.modules.locationHeatmap.legend.title', {}, 'Aktivitetsnivå:')}</div>
                <div className="flex items-center space-x-3 text-xs">
                    <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: 'rgb(34, 197, 94)' }} />
                        <span>{t('manager.modules.locationHeatmap.legend.low', {}, '1 - Lav')}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: 'rgb(234, 179, 8)' }} />
                        <span>{t('manager.modules.locationHeatmap.legend.medium', {}, '2-3 - Middels')}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: 'rgb(249, 115, 22)' }} />
                        <span>{t('manager.modules.locationHeatmap.legend.high', {}, '4 - Høy')}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: 'rgb(220, 38, 38)' }} />
                        <span>{t('manager.modules.locationHeatmap.legend.veryHigh', {}, '5 - Meget høy')}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 bg-gray-100 rounded border border-gray-300" />
                        <span>{t('manager.modules.locationHeatmap.cell.noActivity', {}, 'Ingen aktivitet')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
